import { type CompanyDto, DOC_TYPES, type MeDto, type SeriesDto, validatePattern } from '@bqcrm/shared';
import { Alert, Button, Card, Code, Group, Modal, Stack, Table, Text, TextInput } from '@mantine/core';
import { IconHash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { CompanyBadge } from '../components/CompanyBadge';
import { PageHeader } from '../components/PageHeader';
import { QueryState } from '../components/QueryState';
import { EmptyState } from '../components/ui';
import { notifyError, notifyOk } from '../notify';
import { minHint } from '../minHint';

function EditSeries({ series, onDone }: { series: SeriesDto; onDone: () => void }) {
  const [pattern, setPattern] = useState(series.pattern);
  const [reason, setReason] = useState('');
  const qc = useQueryClient();
  const problems = validatePattern(pattern, DOC_TYPES[series.docType].perCompany);
  const save = useMutation({
    mutationFn: () => api(`/numbering/series/${series.id}`, { method: 'PUT', body: { pattern, reason } }),
    onSuccess: () => {
      notifyOk('Zmieniono wzór numeracji');
      void qc.invalidateQueries({ queryKey: ['series'] });
      onDone();
    },
    onError: notifyError,
  });

  return (
    <Stack>
      <Alert color="yellow" variant="light">
        Zmiana dotyczy tylko numerów nadawanych od teraz. Wydane dokumenty zachowują swoje numery. Licznik roczny nie jest zerowany.
      </Alert>
      <TextInput label="Wzór" value={pattern} onChange={(e) => setPattern(e.currentTarget.value)} error={problems.join(' ') || undefined} />
      <Text size="xs" c="dimmed">
        Znaczniki: <Code>{'{FIRMA}'}</Code> skrót firmy, <Code>{'{RRRR}'}</Code> / <Code>{'{RR}'}</Code> rok, <Code>{'{NNNN}'}</Code> numer kolejny (liczba
        znaków N = minimalna liczba cyfr).
      </Text>
      <TextInput label="Powód zmiany" description={minHint(reason)} value={reason} onChange={(e) => setReason(e.currentTarget.value)} required />
      <Group justify="flex-end">
        <Button variant="default" onClick={onDone}>
          Anuluj
        </Button>
        <Button disabled={problems.length > 0 || reason.trim().length < 3} loading={save.isPending} onClick={() => save.mutate()}>
          Zapisz
        </Button>
      </Group>
    </Stack>
  );
}

export function NumberingPage(_: { me: MeDto }) {
  const q = useQuery({ queryKey: ['series'], queryFn: () => api<SeriesDto[]>('/numbering/series') });
  const companies = useQuery({ queryKey: ['companies'], queryFn: () => api<CompanyDto[]>('/companies') });
  const [editing, setEditing] = useState<SeriesDto | null>(null);
  const company = (id: string | null) => companies.data?.find((c) => c.id === id);

  return (
    <>
      <PageHeader
        title="Numeracja dokumentów"
        description="Oferty i umowy mają osobne serie dla każdej firmy, z licznikiem rocznym. Sprawy i zadania mają numerację wspólną — to nie są dokumenty księgowe. Numer jest nadawany dopiero przy wydaniu dokumentu."
      />
      <Card padding={0}>
        <QueryState isLoading={q.isLoading} error={q.error}>
          <Table.ScrollContainer minWidth={640}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Dokument</Table.Th>
                  <Table.Th>Firma</Table.Th>
                  <Table.Th>Wzór</Table.Th>
                  <Table.Th ta="right">Nadano w tym roku</Table.Th>
                  <Table.Th>Następny numer</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {q.data?.map((s) => {
                  const c = company(s.companyId);
                  return (
                    <Table.Tr key={s.id}>
                      <Table.Td>{s.docTypeLabel}</Table.Td>
                      <Table.Td>{c ? <CompanyBadge company={c} full /> : <Text size="sm" c="dimmed">wspólna</Text>}</Table.Td>
                      <Table.Td>
                        <Code>{s.pattern}</Code>
                      </Table.Td>
                      <Table.Td ta="right">{s.lastValueThisYear}</Table.Td>
                      <Table.Td>
                        <Code>{s.nextNumberPreview}</Code>
                      </Table.Td>
                      <Table.Td>
                        <Button size="xs" variant="subtle" onClick={() => setEditing(s)}>
                          Zmień wzór
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {q.data && q.data.length === 0 && <EmptyState compact icon={<IconHash size={18} />} title="Brak serii numeracji" hint="Serie powstają automatycznie przy dodaniu firmy (Ustawienia → Firmy)." />}
        </QueryState>
      </Card>
      <Modal opened={!!editing} onClose={() => setEditing(null)} title={editing ? `Wzór numeracji: ${editing.docTypeLabel}` : ''}>
        {editing && <EditSeries series={editing} onDone={() => setEditing(null)} />}
      </Modal>
    </>
  );
}
